import * as React from 'react';

import type {User} from 'nutshell-core/types';

import {PopoverNoPortal} from 'shells/popover/popover';

import {useGetSearchResults} from '../../graphql/hooks/use-get-search-results';

import {Footer} from './footer';
import {PopoverContent} from './popover-content';

import styles from './search-bar-popover.module.css';

const SEARCH_BAR_POPOVER_STYLES = {
    maxWidth: '1000px',
    height: 'min(520px, calc(100vh - 70px))',
    minHeight: '200px',
    marginTop: '2px',
} as const;

type Props = {
    user: User | null | undefined;
    searchBarRef: HTMLElement | null | undefined;
    setIsSearchBarFocused: (isSearchBarFocused: boolean) => void;
    searchValue: string;
    setSearchValue: (searchValue: string) => void;
    isTyping: boolean;
    anchor: HTMLElement | null | undefined;
    hasTyped: boolean;
};

export const SearchBarPopover = (props: Props) => {
    const {
        leadResults,
        accountResults,
        contactResults,
        recentResults,
        isLoading: isSearching,
    } = useGetSearchResults({
        query: props.searchValue,
    });

    const isLoading = props.isTyping || isSearching;

    const hasRecentResults = Boolean(
        (recentResults && recentResults.accounts.length > 0) ||
            (recentResults && recentResults.contacts.length > 0) ||
            (recentResults && recentResults.leads.length > 0)
    );

    const hasSearchResults =
        (leadResults && leadResults.length > 0) ||
        (accountResults && accountResults.length > 0) ||
        (contactResults && contactResults.length > 0);

    return (
        <PopoverNoPortal
            anchor={props.anchor}
            onBlur={() => {
                props.setIsSearchBarFocused(false);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                props.searchBarRef && props.searchBarRef.blur();
                props.setSearchValue('');
            }}
            location='bottom-right'
            hideNub={true}
            offsetPosition={-10}
            customStyles={{
                ...SEARCH_BAR_POPOVER_STYLES,
                width:
                    props.user && props.user.isMainSidebarCollapsed
                        ? 'calc(100vw - 80px)'
                        : 'calc(100vw - 210px)',
            }}
            disableFocusTrapZone={true}
            disableOverlayClickToBlur={true}
        >
            <div className={styles['search-bar-popover-container']}>
                <PopoverContent
                    isLoading={isLoading}
                    hasSearchResults={hasSearchResults}
                    hasRecentResults={hasRecentResults}
                    searchValue={props.searchValue}
                    setIsSearchBarFocused={props.setIsSearchBarFocused}
                    accountResults={accountResults}
                    contactResults={contactResults}
                    leadResults={leadResults}
                    recentResults={recentResults}
                    setSearchValue={props.setSearchValue}
                    hasTyped={props.hasTyped}
                />
                {/* Footer is only shown if some results are being displayed or while loading */}
                {isLoading ||
                    ((hasSearchResults || (hasRecentResults && !props.searchValue)) && <Footer />)}
            </div>
        </PopoverNoPortal>
    );
};
